import { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Lock, LogOut, Plus, Pencil, Trash2, Upload, Loader2, X, Check, Flame, PackageCheck, Search, Download } from "lucide-react";
import { StockCheck } from "@/components/admin/StockCheck";
import { ImportStockButton } from "@/components/admin/ImportStock";

const SORTS = {
  category: { label: "Category A–Z", fn: (a, b) => a.tag.localeCompare(b.tag) || a.name.localeCompare(b.name) },
  name: { label: "Name A–Z", fn: (a, b) => a.name.localeCompare(b.name) },
  stock_asc: { label: "Stock: low → high", fn: (a, b) => a.stock - b.stock || a.name.localeCompare(b.name) },
  stock_desc: { label: "Stock: high → low", fn: (a, b) => b.stock - a.stock || a.name.localeCompare(b.name) },
  price_asc: { label: "Price: low → high", fn: (a, b) => a.price_cents - b.price_cents },
  price_desc: { label: "Price: high → low", fn: (a, b) => b.price_cents - a.price_cents },
  newest: { label: "Newest first", fn: (a, b) => (b.created_at || "").localeCompare(a.created_at || "") },
};

const filterProducts = (products, { query, sort, stockFilter }) => {
  const q = query.trim().toLowerCase();
  return products
    .filter((p) => stockFilter === "all" || (stockFilter === "soldout" ? p.stock === 0 : p.stock > 0 && p.stock <= 3))
    .filter((p) => !q || [p.name, p.sku, p.upc, p.tag, p.keywords, p.vendor].some((v) => (v || "").toLowerCase().includes(q)))
    .sort(SORTS[sort].fn);
};

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (cents) =>
  new Intl.NumberFormat("en-CA", { style: "currency", currency: "CAD" }).format(cents / 100);

const emptyForm = { name: "", blurb: "", price: "", salePrice: "", saleEndsAt: "", tag: "Candy", stock: 0, image: "", trending: false, sku: "", cost: "", vendor: "", notes: "", keywords: "", upc: "" };

function LoginForm({ onLogin }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/auth/login`, { email, password }, { withCredentials: true });
      onLogin(data);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Login failed. Check your credentials.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7] px-5">
      <motion.form
        onSubmit={submit}
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        data-testid="admin-login-form"
        className="hard-shadow-lg w-full max-w-md rounded-2xl border-2 border-[#0A0A0A] bg-white p-10"
      >
        <Lock className="h-10 w-10 text-[#FF2A54]" />
        <h1 className="mt-4 font-display text-3xl font-extrabold uppercase tracking-tight">Store admin</h1>
        <p className="mt-2 text-sm text-[#4A4A4A]">Sign in to manage products, prices and stock.</p>
        <input
          type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
          placeholder="Email" data-testid="admin-email-input"
          className="hard-shadow mt-6 w-full rounded-full border-2 border-[#0A0A0A] px-5 py-3 text-sm font-medium outline-none focus:ring-4 focus:ring-[#FF2A54]/40"
        />
        <input
          type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
          placeholder="Password" data-testid="admin-password-input"
          className="hard-shadow mt-4 w-full rounded-full border-2 border-[#0A0A0A] px-5 py-3 text-sm font-medium outline-none focus:ring-4 focus:ring-[#FF2A54]/40"
        />
        <button
          type="submit" disabled={loading} data-testid="admin-login-button"
          className="hard-shadow hard-shadow-hover mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-[#FF2A54] px-8 py-3.5 font-display text-sm font-extrabold uppercase text-white disabled:opacity-60"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Sign in
        </button>
      </motion.form>
    </div>
  );
}

function ProductForm({ form, setForm, onSubmit, onCancel, saving, editing }) {
  const [uploading, setUploading] = useState(false);
  const [categories, setCategories] = useState(["Candy", "Toy"]);

  useEffect(() => {
    axios.get(`${API}/admin/categories`, { withCredentials: true })
      .then((res) => setCategories(res.data.categories))
      .catch(() => {});
  }, []);

  const onTagChange = async (e) => {
    const v = e.target.value;
    if (v !== "__new__") {
      setForm({ ...form, tag: v });
      return;
    }
    const name = window.prompt("New category name:");
    if (!name || !name.trim()) return;
    try {
      const { data } = await axios.post(`${API}/admin/categories`, { name: name.trim() }, { withCredentials: true });
      setCategories(data.categories);
      setForm({ ...form, tag: data.name });
      toast.success(`Category "${data.name}" added`);
    } catch {
      toast.error("Could not add category");
    }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, fd, { withCredentials: true });
      setForm((f) => ({ ...f, image: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  const inputCls = "hard-shadow w-full rounded-xl border-2 border-[#0A0A0A] px-4 py-3 text-sm font-medium outline-none focus:ring-4 focus:ring-[#FF2A54]/40";

  return (
    <form onSubmit={onSubmit} data-testid="product-form" className="hard-shadow-lg rounded-2xl border-2 border-[#0A0A0A] bg-white p-6 sm:p-8">
      <div className="flex items-center justify-between">
        <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
          {editing ? "Edit product" : "Add product"}
        </h2>
        {editing && (
          <button type="button" onClick={onCancel} data-testid="product-form-cancel" aria-label="Cancel edit"
            className="rounded-full border-2 border-[#0A0A0A] p-1.5 hover:bg-[#FFD700]">
            <X className="h-4 w-4" />
          </button>
        )}
      </div>
      <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2">
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required
          placeholder="Product name" data-testid="product-form-name" className={inputCls} />
        <select value={form.tag} onChange={onTagChange}
          data-testid="product-form-tag" className={inputCls}>
          {!categories.includes(form.tag) && form.tag && <option value={form.tag}>{form.tag}</option>}
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
          <option value="__new__">+ New category…</option>
        </select>
        <textarea value={form.blurb} onChange={(e) => setForm({ ...form, blurb: e.target.value })}
          placeholder="Short description" data-testid="product-form-blurb" rows={2}
          className={`${inputCls} sm:col-span-2`} />
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Price (CAD)</label>
          <input type="number" min="0.5" step="0.01" required value={form.price}
            onChange={(e) => setForm({ ...form, price: e.target.value })} placeholder="12.00"
            data-testid="product-form-price" className={inputCls} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-[#FF2A54]">Sale price (optional)</label>
          <input type="number" min="0" step="0.01" value={form.salePrice}
            onChange={(e) => setForm({ ...form, salePrice: e.target.value })} placeholder="Leave empty for no sale"
            data-testid="product-form-sale-price" className={inputCls} />
          {form.salePrice && form.price && parseFloat(form.salePrice) >= parseFloat(form.price) && (
            <p className="mt-1 text-xs font-bold text-[#FF2A54]">Sale price must be lower than the regular price.</p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-[#FF2A54]">Sale ends (optional)</label>
          <input type="date" value={form.saleEndsAt}
            onChange={(e) => setForm({ ...form, saleEndsAt: e.target.value })}
            data-testid="product-form-sale-ends" className={inputCls} />
          <p className="mt-1 text-[10px] text-[#4A4A4A]">Shoppers see a live &ldquo;sale ends&rdquo; countdown. Sale turns off automatically after this day.</p>
        </div>
        <div>
          <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Stock</label>
          <input type="number" min="0" step="1" required value={form.stock}
            onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="10"
            data-testid="product-form-stock" className={inputCls} />
        </div>
        <div className="sm:col-span-2 mt-2 rounded-xl border-2 border-dashed border-[#0A0A0A]/30 p-4">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Internal details — never shown to shoppers</p>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <input value={form.sku} onChange={(e) => setForm({ ...form, sku: e.target.value })}
              placeholder="SKU (e.g. CBM-0014)" data-testid="product-form-sku" className={inputCls} />
            <div className="flex items-center gap-2">
              <input type="number" min="0" step="0.01" value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                placeholder="Your cost (CAD)" data-testid="product-form-cost" className={inputCls} />
            </div>
            <input value={form.vendor} onChange={(e) => setForm({ ...form, vendor: e.target.value })}
              placeholder="Vendor / supplier" data-testid="product-form-vendor" className={inputCls} />
            <input value={form.upc} onChange={(e) => setForm({ ...form, upc: e.target.value })}
              placeholder="UPC / barcode" data-testid="product-form-upc" className={inputCls} />
            <input value={form.keywords} onChange={(e) => setForm({ ...form, keywords: e.target.value })}
              placeholder="Keywords (comma separated)" data-testid="product-form-keywords" className={`${inputCls} sm:col-span-2`} />
            <textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })}
              placeholder="Internal notes (restock info, supplier links…)" data-testid="product-form-notes" rows={2}
              className={`${inputCls} sm:col-span-2`} />
          </div>
          {form.price && form.cost && parseFloat(form.cost) > 0 && (
            <p data-testid="product-form-margin" className="mt-3 text-xs font-bold">
              Margin:{" "}
              <span className={parseFloat(form.price) - parseFloat(form.cost) >= 0 ? "text-[#16a34a]" : "text-[#FF2A54]"}>
                {fmt(Math.round((parseFloat(form.price) - parseFloat(form.cost)) * 100))}
                {" "}({(((parseFloat(form.price) - parseFloat(form.cost)) / parseFloat(form.price)) * 100).toFixed(0)}%)
              </span>
            </p>
          )}
        </div>
        <div className="sm:col-span-2">
          <label className="mb-1 block text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Photo</label>
          <div className="flex items-center gap-4">
            {form.image && (
              <img src={form.image} alt="Product preview" data-testid="product-form-image-preview"
                className="hard-shadow h-20 w-20 rounded-xl border-2 border-[#0A0A0A] object-cover" />
            )}
            <label data-testid="product-form-image-upload"
              className="hard-shadow hard-shadow-hover inline-flex cursor-pointer items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-[#FFD700] px-5 py-2.5 font-display text-xs font-extrabold uppercase">
              {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />}
              {uploading ? "Uploading" : form.image ? "Change photo" : "Upload photo"}
              <input type="file" accept="image/*" className="hidden" onChange={upload} disabled={uploading} />
            </label>
          </div>
        </div>
      </div>
      <button type="submit" disabled={saving || uploading || !form.image} data-testid="product-form-submit"
        className="hard-shadow hard-shadow-hover mt-6 inline-flex items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-[#FF2A54] px-8 py-3 font-display text-sm font-extrabold uppercase text-white disabled:opacity-60">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
        {editing ? "Save changes" : "Add to store"}
      </button>
      {!form.image && <p className="mt-2 text-xs text-[#4A4A4A]">A photo is required.</p>}
    </form>
  );
}

const STATUS_STYLES = {
  paid: "bg-[#22c55e] text-white",
  pending: "bg-[#FFD700] text-[#0A0A0A]",
  failed: "bg-[#FF2A54] text-white",
  expired: "bg-[#4A4A4A] text-white",
  refunded: "bg-[#4A4A4A] text-white",
};

const CARRIERS = ["Canada Post", "Purolator", "UPS", "FedEx", "USPS", "Other"];

function ShipDialog({ order, onClose, onShipped }) {
  const [carrier, setCarrier] = useState("Canada Post");
  const [tracking, setTracking] = useState("");
  const [url, setUrl] = useState("");
  const [loading, setLoading] = useState(false);
  const short = order.session_id.slice(-8);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = { carrier, tracking_number: tracking.trim(), tracking_url: carrier === "Other" ? url.trim() : "" };
      const { data } = await axios.post(`${API}/admin/orders/${order.session_id}/ship`, body, { withCredentials: true });
      toast.success(data.email_sent ? "Marked shipped — buyer emailed" : "Marked shipped (no buyer email on file)");
      onShipped();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not mark as shipped");
    } finally {
      setLoading(false);
    }
  };

  const inputCls = "w-full rounded-lg border-2 border-[#0A0A0A] bg-white px-3 py-2 text-sm font-medium outline-none focus:bg-[#FFF7DB]";
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#0A0A0A]/60 p-4" data-testid="ship-dialog">
      <form onSubmit={submit} className="hard-shadow w-full max-w-md rounded-2xl border-2 border-[#0A0A0A] bg-white p-6">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-display text-xl font-extrabold tracking-tight">Mark as shipped</h3>
            <p className="mt-1 text-xs text-[#4A4A4A]">Order …{short} · {order.buyer_email || "no buyer email"}</p>
          </div>
          <button type="button" onClick={onClose} data-testid="ship-dialog-close" className="rounded-full border-2 border-[#0A0A0A] p-1 hover:bg-[#FFD700]"><X className="h-4 w-4" /></button>
        </div>
        <label className="mt-5 block text-xs font-extrabold uppercase tracking-wide">Carrier
          <select value={carrier} onChange={(e) => setCarrier(e.target.value)} data-testid="ship-carrier" className={`mt-1 ${inputCls}`}>
            {CARRIERS.map((c) => <option key={c}>{c}</option>)}
          </select>
        </label>
        <label className="mt-3 block text-xs font-extrabold uppercase tracking-wide">Tracking number <span className="font-medium normal-case text-[#4A4A4A]">(optional)</span>
          <input value={tracking} onChange={(e) => setTracking(e.target.value)} placeholder="e.g. 7123456789012345" data-testid="ship-tracking" className={`mt-1 ${inputCls}`} />
        </label>
        {carrier === "Other" && (
          <label className="mt-3 block text-xs font-extrabold uppercase tracking-wide">Tracking link <span className="font-medium normal-case text-[#4A4A4A]">(optional)</span>
            <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://…" data-testid="ship-url" className={`mt-1 ${inputCls}`} />
          </label>
        )}
        <p className="mt-3 text-xs text-[#4A4A4A]">
          {tracking.trim() && carrier !== "Other" ? `The buyer's email will include a "Track your package" link to ${carrier}.` : "The buyer will get a shipping email."}
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="rounded-full border-2 border-[#0A0A0A] px-4 py-2 text-xs font-extrabold uppercase hover:bg-[#FFD700]">Cancel</button>
          <button type="submit" disabled={loading} data-testid="ship-dialog-submit"
            className="hard-shadow hard-shadow-hover inline-flex items-center gap-1.5 rounded-full border-2 border-[#0A0A0A] bg-[#0A0A0A] px-4 py-2 text-xs font-extrabold uppercase text-white disabled:opacity-60">
            {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <PackageCheck className="h-3.5 w-3.5" />} Ship &amp; email buyer
          </button>
        </div>
      </form>
    </div>
  );
}

function OrdersList() {
  const [orders, setOrders] = useState(null);
  const [shipping, setShipping] = useState(null);

  const load = () =>
    axios.get(`${API}/admin/orders`, { withCredentials: true })
      .then((res) => setOrders(res.data.orders))
      .catch(() => toast.error("Could not load orders"));

  useEffect(() => { load(); }, []);

  if (orders === null) {
    return <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-[#FF2A54]" /></div>;
  }
  if (orders.length === 0) {
    return (
      <p data-testid="orders-empty" className="hard-shadow rounded-2xl border-2 border-[#0A0A0A] bg-white p-10 text-center text-sm font-medium text-[#4A4A4A]">
        No orders yet. They&rsquo;ll show up here the moment someone checks out.
      </p>
    );
  }
  return (
    <div data-testid="orders-list" className="flex flex-col gap-6">
      {shipping && <ShipDialog order={shipping} onClose={() => setShipping(null)} onShipped={() => { setShipping(null); load(); }} />}
      {orders.map((o) => (
        <article key={o.session_id} data-testid={`order-${o.session_id.slice(-8)}`}
          className="hard-shadow rounded-2xl border-2 border-[#0A0A0A] bg-white p-5 sm:p-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="font-display text-lg font-extrabold tracking-tight">
                {fmt(o.amount)} <span className="text-sm font-bold text-[#4A4A4A]">CAD</span>
              </p>
              <p className="text-xs text-[#4A4A4A]">
                {new Date(o.created_at).toLocaleString("en-CA", { dateStyle: "medium", timeStyle: "short" })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {o.fulfillment_status === "shipped" ? (
                <span data-testid={`order-shipped-${o.session_id.slice(-8)}`}
                  className="inline-flex items-center gap-1.5 rounded-full border-2 border-[#0A0A0A] bg-[#22c55e] px-3 py-1 text-xs font-extrabold uppercase text-white">
                  <PackageCheck className="h-3.5 w-3.5" /> Shipped
                </span>
              ) : (
                o.payment_status === "paid" && (
                  <button onClick={() => setShipping(o)}
                    data-testid={`order-ship-${o.session_id.slice(-8)}`}
                    className="hard-shadow hard-shadow-hover inline-flex items-center gap-1.5 rounded-full border-2 border-[#0A0A0A] bg-white px-3 py-1 text-xs font-extrabold uppercase hover:bg-[#FFD700]">
                    <PackageCheck className="h-3.5 w-3.5" />
                    Mark shipped
                  </button>
                )
              )}
              <span data-testid={`order-status-${o.session_id.slice(-8)}`}
                className={`rounded-full border-2 border-[#0A0A0A] px-3 py-1 text-xs font-extrabold uppercase ${STATUS_STYLES[o.payment_status] || "bg-white"}`}>
                {o.payment_status}
              </span>
            </div>
          </div>
          <div className="mt-3 text-sm font-medium" data-testid={`order-buyer-${o.session_id.slice(-8)}`}>
            <p>
              Buyer:{" "}
              {o.buyer_name && <span className="font-bold">{o.buyer_name} · </span>}
              {o.buyer_email ? (
                <a href={`mailto:${o.buyer_email}`} data-testid={`order-email-${o.session_id.slice(-8)}`}
                  className="font-bold underline decoration-[#FFD700] decoration-2 underline-offset-2">{o.buyer_email}</a>
              ) : (
                <span className="text-[#4A4A4A]">—</span>
              )}
              {o.buyer_phone && <span className="text-[#4A4A4A]"> · {o.buyer_phone}</span>}
            </p>
            {o.shipping_address && (
              <p className="mt-1 text-xs text-[#4A4A4A]" data-testid={`order-address-${o.session_id.slice(-8)}`}>
                Ship to: {o.shipping_address}
              </p>
            )}
            {o.fulfillment_status === "shipped" && (o.tracking_number || o.tracking_url) && (
              <p className="mt-1 text-xs font-bold" data-testid={`order-tracking-${o.session_id.slice(-8)}`}>
                {o.carrier || "Tracking"}{o.tracking_number ? `: ${o.tracking_number}` : ""}
                {o.tracking_url && (
                  <a href={o.tracking_url} target="_blank" rel="noreferrer" className="ml-2 underline decoration-[#FFD700] decoration-2 underline-offset-2">Track →</a>
                )}
              </p>
            )}
          </div>
          <ul className="mt-3 flex flex-col gap-1 border-t-2 border-dashed border-[#0A0A0A]/20 pt-3">
            {(o.items || []).map((i) => (
              <li key={i.lookup_key} className="flex justify-between text-sm">
                <span>{i.name} <span className="text-[#4A4A4A]">× {i.quantity}</span></span>
                <span className="font-bold">{fmt(i.unit_amount * i.quantity)}</span>
              </li>
            ))}
          </ul>
        </article>
      ))}
    </div>
  );
}

function InventoryRow({ p, onEdit, onRemove, onSaved }) {
  const [price, setPrice] = useState((p.price_cents / 100).toFixed(2));
  const [stock, setStock] = useState(String(p.stock));
  const [saving, setSaving] = useState(false);
  const [toggling, setToggling] = useState(false);

  useEffect(() => {
    setPrice((p.price_cents / 100).toFixed(2));
    setStock(String(p.stock));
  }, [p.price_cents, p.stock]);

  const dirty =
    Math.round(parseFloat(price || "0") * 100) !== p.price_cents ||
    (parseInt(stock, 10) || 0) !== p.stock;

  const fullPayload = (overrides = {}) => ({
    name: p.name, blurb: p.blurb, tag: p.tag, image: p.image, trending: !!p.trending,
    sale_price_cents: p.sale_price_cents || 0,
    sale_ends_at: p.sale_ends_at || "",
    sku: p.sku || "", cost_cents: p.cost_cents || 0, vendor: p.vendor || "",
    notes: p.notes || "", keywords: p.keywords || "", upc: p.upc || "",
    price_cents: p.price_cents, stock: p.stock,
    ...overrides,
  });

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/products/${p.id}`, fullPayload({
        price_cents: Math.round(parseFloat(price) * 100),
        stock: parseInt(stock, 10) || 0,
      }), { withCredentials: true });
      toast.success(`${p.name} updated`);
      onSaved();
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleTrending = async () => {
    setToggling(true);
    try {
      await axios.put(`${API}/admin/products/${p.id}`, fullPayload({ trending: !p.trending }), { withCredentials: true });
      toast.success(p.trending ? `${p.name} removed from Trending` : `${p.name} is now Trending`);
      onSaved();
    } catch {
      toast.error("Could not update trending");
    } finally {
      setToggling(false);
    }
  };

  const cellInput = "hard-shadow w-24 rounded-lg border-2 border-[#0A0A0A] px-2.5 py-1.5 text-sm font-bold outline-none focus:ring-4 focus:ring-[#FF2A54]/40";

  return (
    <tr data-testid={`product-row-${p.slug}`} className="border-b-2 border-[#0A0A0A]/10 last:border-0">
      <td className="py-3 pl-4 pr-3">
        <img src={p.image} alt={p.name} className="hard-shadow h-12 w-12 rounded-lg border-2 border-[#0A0A0A] object-cover" />
      </td>
      <td className="px-3 py-3">
        <p className="font-display text-sm font-bold leading-tight">{p.name}</p>
        {p.sku && <span className="text-[10px] font-bold uppercase tracking-wide text-[#4A4A4A]">SKU {p.sku}</span>}
        {p.stock === 0 && (
          <span data-testid={`product-stock-${p.slug}`} className="block text-xs font-bold text-[#FF2A54]">Sold out</span>
        )}
      </td>
      <td className="px-3 py-3">
        <span className="rounded-full border-2 border-[#0A0A0A] bg-[#FFD700] px-2.5 py-0.5 text-[10px] font-bold uppercase whitespace-nowrap">{p.tag}</span>
      </td>
      <td className="px-3 py-3">
        <div className="flex items-center gap-1">
          <span className="text-sm font-bold text-[#4A4A4A]">$</span>
          <input type="number" min="0.5" step="0.01" value={price} onChange={(e) => setPrice(e.target.value)}
            data-testid={`inventory-price-${p.slug}`} className={cellInput} />
        </div>
        {p.sale_price_cents > 0 && p.sale_price_cents < p.price_cents && (
          <p data-testid={`inventory-sale-${p.slug}`} className="mt-1 text-[10px] font-black uppercase text-[#FF2A54]">
            Sale {fmt(p.sale_price_cents)}
          </p>
        )}
      </td>
      <td className="px-3 py-3">
        <input type="number" min="0" step="1" value={stock} onChange={(e) => setStock(e.target.value)}
          data-testid={`inventory-stock-${p.slug}`} className={cellInput} />
      </td>
      <td className="px-3 py-3" data-testid={`inventory-margin-${p.slug}`}>
        {p.cost_cents > 0 ? (
          <div className="text-xs font-bold leading-tight">
            <p className="text-[#4A4A4A]">cost {fmt(p.cost_cents)}</p>
            <p className={p.price_cents - p.cost_cents >= 0 ? "text-[#16a34a]" : "text-[#FF2A54]"}>
              +{fmt(p.price_cents - p.cost_cents)} ({Math.round(((p.price_cents - p.cost_cents) / p.price_cents) * 100)}%)
            </p>
          </div>
        ) : (
          <span className="text-xs text-[#4A4A4A]">—</span>
        )}
      </td>
      <td className="py-3 pl-3 pr-4">
        <div className="flex items-center justify-end gap-2">
          <button onClick={toggleTrending} disabled={toggling} data-testid={`inventory-trending-${p.slug}`}
            title={p.trending ? "Remove from Trending" : "Mark as Trending"}
            className={`hard-shadow hard-shadow-hover rounded-full border-2 border-[#0A0A0A] p-2 disabled:opacity-60 ${
              p.trending ? "bg-[#FF7A00] text-white" : "bg-white text-[#4A4A4A]"
            }`}>
            <Flame className="h-3.5 w-3.5" />
          </button>
          {dirty && (
            <button onClick={save} disabled={saving} data-testid={`inventory-save-${p.slug}`}
              className="hard-shadow hard-shadow-hover inline-flex items-center gap-1.5 rounded-full border-2 border-[#0A0A0A] bg-[#22c55e] px-4 py-1.5 font-display text-xs font-extrabold uppercase text-white disabled:opacity-60">
              {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />} Save
            </button>
          )}
          <button onClick={() => onEdit(p)} data-testid={`product-edit-${p.slug}`} title="Edit details & photo"
            className="hard-shadow hard-shadow-hover rounded-full border-2 border-[#0A0A0A] bg-[#FFD700] p-2">
            <Pencil className="h-3.5 w-3.5" />
          </button>
          <button onClick={() => onRemove(p)} data-testid={`product-delete-${p.slug}`} title="Remove product"
            className="hard-shadow hard-shadow-hover rounded-full border-2 border-[#0A0A0A] bg-[#FF2A54] p-2 text-white">
            <Trash2 className="h-3.5 w-3.5" />
          </button>
        </div>
      </td>
    </tr>
  );
}

function ProductManager({ onLogout }) {
  const [tab, setTab] = useState("orders");
  const [products, setProducts] = useState([]);
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("category");
  const [stockFilter, setStockFilter] = useState("all");
  const visible = filterProducts(products, { query, sort, stockFilter });
  const soldOutCount = products.filter((p) => p.stock === 0).length;
  const [exporting, setExporting] = useState(false);

  const exportCsv = async () => {
    setExporting(true);
    try {
      const res = await axios.get(`${API}/admin/products/export.csv`, { withCredentials: true, responseType: "blob" });
      const name = (res.headers["content-disposition"] || "").match(/filename="([^"]+)"/)?.[1] || "inventory.csv";
      const url = URL.createObjectURL(res.data);
      const a = Object.assign(document.createElement("a"), { href: url, download: name });
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Inventory spreadsheet downloaded");
    } catch {
      toast.error("Export failed");
    } finally {
      setExporting(false);
    }
  };

  const load = () =>
    axios.get(`${API}/admin/products`, { withCredentials: true })
      .then((res) => setProducts(res.data.products))
      .catch(() => toast.error("Could not load products"));

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      name: form.name.trim(), blurb: form.blurb.trim(), tag: form.tag,
      price_cents: Math.round(parseFloat(form.price) * 100),
      sale_price_cents: form.salePrice ? Math.round(parseFloat(form.salePrice) * 100) : 0,
      sale_ends_at: form.saleEndsAt ? new Date(`${form.saleEndsAt}T23:59:59`).toISOString() : "",
      stock: parseInt(form.stock, 10) || 0, image: form.image, trending: !!form.trending,
      sku: form.sku.trim(), vendor: form.vendor.trim(), notes: form.notes.trim(),
      keywords: form.keywords.trim(), upc: form.upc.trim(),
      cost_cents: form.cost ? Math.round(parseFloat(form.cost) * 100) : 0,
    };
    try {
      if (editingId) {
        await axios.put(`${API}/admin/products/${editingId}`, payload, { withCredentials: true });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, { withCredentials: true });
        toast.success("Product added to the store");
      }
      setForm(emptyForm);
      setEditingId(null);
      setShowForm(false);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      const msg = typeof d === "string" ? d
        : Array.isArray(d) ? d.map((e) => e.msg || "").filter(Boolean).join("; ")
        : "";
      toast.error(msg || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const edit = (p) => {
    setEditingId(p.id);
    setForm({
      name: p.name, blurb: p.blurb, price: (p.price_cents / 100).toFixed(2),
      salePrice: p.sale_price_cents ? (p.sale_price_cents / 100).toFixed(2) : "",
      saleEndsAt: p.sale_ends_at ? p.sale_ends_at.slice(0, 10) : "",
      tag: p.tag, stock: p.stock, image: p.image, trending: !!p.trending,
      sku: p.sku || "", cost: p.cost_cents ? (p.cost_cents / 100).toFixed(2) : "",
      vendor: p.vendor || "", notes: p.notes || "", keywords: p.keywords || "", upc: p.upc || "",
    });
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (p) => {
    if (!window.confirm(`Permanently remove "${p.name}" from the store?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { withCredentials: true });
      toast.success("Product removed");
      load();
    } catch {
      toast.error("Remove failed");
    }
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      <header className="border-b-2 border-[#0A0A0A] bg-white">
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-4 sm:px-8">
          <h1 className="font-display text-lg font-extrabold uppercase tracking-tight">
            Store <span className="text-[#FF2A54]">Admin</span>
          </h1>
          <div className="flex items-center gap-3">
            <a href="/" data-testid="admin-view-store-link" className="text-xs font-bold uppercase tracking-wide underline decoration-[#FFD700] decoration-4 underline-offset-4">
              View store
            </a>
            <button onClick={onLogout} data-testid="admin-logout-button"
              className="hard-shadow hard-shadow-hover inline-flex items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-white px-4 py-2 font-display text-xs font-extrabold uppercase">
              <LogOut className="h-3.5 w-3.5" /> Log out
            </button>
          </div>
        </div>
      </header>

      <main data-testid="admin-dashboard" className="mx-auto max-w-6xl px-5 py-10 sm:px-8">
        <div className="mb-10 flex gap-3">
          {[["orders", "Orders"], ["products", "Products"], ["stock", "Stock check"]].map(([key, label]) => (
            <button key={key} onClick={() => setTab(key)} data-testid={`admin-tab-${key}`}
              className={`hard-shadow hard-shadow-hover relative rounded-full border-2 border-[#0A0A0A] px-6 py-2.5 font-display text-sm font-extrabold uppercase ${
                tab === key ? "bg-[#FF2A54] text-white" : "bg-white"
              }`}>
              {label}
              {key === "stock" && soldOutCount > 0 && (
                <span data-testid="stock-tab-badge" className="absolute -right-2 -top-2 rounded-full border-2 border-[#0A0A0A] bg-[#FFD700] px-2 py-0.5 text-[10px] font-extrabold text-[#0A0A0A]">{soldOutCount}</span>
              )}
            </button>
          ))}
        </div>

        {tab === "orders" ? (
          <OrdersList />
        ) : tab === "stock" ? (
          <StockCheck products={products} onSaved={load} />
        ) : (
          <>
            <div className="flex flex-wrap items-center justify-between gap-4">
              <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
                Inventory <span className="text-[#FF2A54]">({products.length})</span>
              </h2>
              {!showForm && (
                <div className="flex flex-wrap items-center gap-3">
                  <ImportStockButton onImported={load} />
                  <button onClick={exportCsv} disabled={exporting} data-testid="inventory-export-button"
                    className="hard-shadow hard-shadow-hover inline-flex items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-white px-5 py-2.5 font-display text-sm font-extrabold uppercase disabled:opacity-60">
                    {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Export CSV
                  </button>
                  <button onClick={() => { setEditingId(null); setForm(emptyForm); setShowForm(true); }}
                    data-testid="inventory-add-button"
                    className="hard-shadow hard-shadow-hover inline-flex items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-[#FF2A54] px-6 py-2.5 font-display text-sm font-extrabold uppercase text-white">
                    <Plus className="h-4 w-4" /> Add product
                  </button>
                </div>
              )}
            </div>

            {showForm && (
              <div className="mt-6">
                <ProductForm form={form} setForm={setForm} onSubmit={submit}
                  onCancel={() => { setEditingId(null); setForm(emptyForm); setShowForm(false); }}
                  saving={saving} editing={!!editingId} />
              </div>
            )}

            <div className="mt-6 flex flex-wrap items-center gap-3" data-testid="inventory-toolbar">
              <label className="hard-shadow flex min-w-[240px] flex-1 items-center gap-2 rounded-full border-2 border-[#0A0A0A] bg-white px-4 py-2">
                <Search className="h-4 w-4 text-[#4A4A4A]" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search name, SKU, UPC, category…"
                  data-testid="inventory-search" className="w-full bg-transparent text-sm font-bold outline-none placeholder:font-medium" />
                {query && <button onClick={() => setQuery("")} data-testid="inventory-search-clear"><X className="h-4 w-4" /></button>}
              </label>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="inventory-sort"
                className="hard-shadow rounded-full border-2 border-[#0A0A0A] bg-white px-4 py-2 text-xs font-extrabold uppercase outline-none">
                {Object.entries(SORTS).map(([k, s]) => <option key={k} value={k}>Sort: {s.label}</option>)}
              </select>
              <div className="flex gap-2">
                {[["all", "All"], ["low", "Low stock"], ["soldout", `Sold out${soldOutCount ? ` (${soldOutCount})` : ""}`]].map(([k, label]) => (
                  <button key={k} onClick={() => setStockFilter(k)} data-testid={`inventory-filter-${k}`}
                    className={`hard-shadow hard-shadow-hover rounded-full border-2 border-[#0A0A0A] px-4 py-2 text-xs font-extrabold uppercase ${
                      stockFilter === k ? "bg-[#0A0A0A] text-white" : "bg-white"}`}>
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <div data-testid="inventory-table" className="hard-shadow mt-4 overflow-x-auto rounded-2xl border-2 border-[#0A0A0A] bg-white">
              <table className="w-full min-w-[640px] text-left">
                <thead>
                  <tr className="border-b-2 border-[#0A0A0A] bg-[#FFF7DB]">
                    <th className="py-3 pl-4 pr-3 text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Photo</th>
                    <th className="px-3 py-3 text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Product</th>
                    <th className="px-3 py-3 text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Category</th>
                    <th className="px-3 py-3 text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Price (CAD)</th>
                    <th className="px-3 py-3 text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Stock</th>
                    <th className="px-3 py-3 text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Cost / Margin</th>
                    <th className="py-3 pl-3 pr-4 text-right text-xs font-bold uppercase tracking-widest text-[#4A4A4A]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {visible.map((p) => (
                    <InventoryRow key={p.id} p={p} onEdit={edit} onRemove={remove} onSaved={load} />
                  ))}
                </tbody>
              </table>
              {products.length === 0 && (
                <p className="p-10 text-center text-sm font-medium text-[#4A4A4A]">No products yet. Click &ldquo;Add product&rdquo; to stock your store.</p>
              )}
              {products.length > 0 && visible.length === 0 && (
                <p data-testid="inventory-no-match" className="p-10 text-center text-sm font-medium text-[#4A4A4A]">No products match this search or filter.</p>
              )}
            </div>
            <p className="mt-3 text-xs text-[#4A4A4A]">Tip: change price or stock right in the row, then hit Save. Use Edit to change the name, photo, category or description.</p>
          </>
        )}
      </main>
    </div>
  );
}

export default function Admin() {
  const [auth, setAuth] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then((res) => setAuth(res.data))
      .catch(() => setAuth(false));
  }, []);

  const logout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    setAuth(false);
  };

  if (auth === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-[#FDFBF7]">
        <Loader2 className="h-8 w-8 animate-spin text-[#FF2A54]" />
      </div>
    );
  }
  if (!auth) return <LoginForm onLogin={setAuth} />;
  return <ProductManager onLogout={logout} />;
}
